import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { useI18n } from '../i18n/context.js';
import { useUI } from '../state/ui.js';
import { useTopBarSlot } from '../state/topbarSlot.js';
import { IconButton } from './IconButton.js';

export interface TopBarProps {
  title?: string;
  center?: ReactNode;
  children?: ReactNode;
}

/** App-wide top bar: hamburger menu, section title/center slot, and chat actions. */
export function TopBar({ title, center, children }: TopBarProps) {
  const { t } = useI18n();
  const { openDrawer, newChat, section } = useUI();
  const { setEl } = useTopBarSlot();
  const slotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setEl(slotRef.current);
    return () => setEl(null);
  }, [setEl]);

  return (
    <header className="topbar">
      <IconButton
        icon="menu"
        label={t('topbar.menu')}
        onClick={openDrawer}
        className="topbar-menu"
      />

      <div className="topbar-center">
        {center ?? <span className="topbar-title">{title ?? t('app.name')}</span>}
      </div>

      <div className="topbar-actions">
        {/* Portal target — section-specific buttons land here (before the + button) */}
        <div ref={slotRef} className="topbar-actions-slot" />
        {section === 'chat' && (
          <IconButton icon="plus" label={t('sidebar.newChat')} onClick={newChat} className="topbar-new-chat" />
        )}
        {children}
      </div>
    </header>
  );
}
